import { TranslocoService, TranslocoPipe } from '@jsverse/transloco';
import { Component, Input, OnInit, inject } from '@angular/core';
import { LossRatioModal } from '../../modals/loss-ratio-modal/loss-ratio-modal.component';
import { FuelKpiRequest } from '../../../../_shared';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { formatPercentageDifference, formatDropdownValue, getEnumFromString } from '../../../../../_shared/helpers/kpiWidget/kpi-formatters.helper';
import { FuelChangesService } from '../../../services/fuel-changes.service';
import { AccountFilterData, DateIntervalFilterData } from '../../../../../_shared/models';
import { BaseWidgetComponent } from '../../../../../home/home-widgets/widgets/base-widget.component';
import { FilterService } from '../../../../../_shared/services';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { isUptrendPositive } from '../../../../../_shared/helpers/kpiWidget/dropdown-measurement.helper';
import { DropdownOption, KpiWidgetComponent } from '../../../../../_shared/components/kpi-widget/kpi-widget.component';
import { MeasurementTypes } from '../../../../../_shared/enums/kpiStats/measurement-types.enum';

@Component({
  selector: 'app-loss-ratio-widget',
  standalone: true,
  imports: [TranslocoPipe, KpiWidgetComponent],
  templateUrl: './loss-ratio-widget.component.html',
  styleUrl: './loss-ratio-widget.component.scss'
})
export class LossRatioWidgetComponent extends BaseWidgetComponent implements OnInit {
  private readonly transloco = inject(TranslocoService);
  @Input() selectedFilter: string;
  @Input() accountFilter: AccountFilterData = {} as AccountFilterData;
  @Input() dateFilter: DateIntervalFilterData = {} as DateIntervalFilterData;
  @Input() modalFilterHeaders: { previous: string; current: string } = { previous: '', current: '' };

  icon: string = 'assets/images-fuel/costIcon.svg';
  lossRatioPercentageChange: any;
  selectedUnit: string = MeasurementTypes.Percentage.toString();
  dropdownOptions: DropdownOption[] = [
    { label: '%', value: MeasurementTypes.Percentage.toString(), unit: '%' }
  ];
  currencySymbol: string = '';
  currencyCode: string = '';
  hasError: boolean = false;
  errorMessage: string = '';
  request: FuelKpiRequest;

  constructor(
    protected override filterService: FilterService,
    private fuelChangesService: FuelChangesService,
    private modalService: NgbModal,
    private errorHandlerService: ErrorHandlerService
  ) {
    super(filterService);
    this.title = 'Loss Ratio';
    this.isLoading = true;
    this.value = 0;
    this.difference = 0;
  }

  ngOnInit(): void { }

  async onFilterChangeAsync(): Promise<void> {
    await this.getFuelLossRatioKpiStats();
  }

  async onDropdownChanged(selectedOption: DropdownOption): Promise<void> {
    this.selectedUnit = selectedOption.value;
  }

  get isUptrendPositive(): boolean {
    return isUptrendPositive(getEnumFromString(this.selectedUnit));
  }

  formatValue(value: number): string {
    return formatDropdownValue(value, this.selectedUnit, this.dropdownOptions, getEnumFromString, this.currencySymbol);
  }

  formatLossRatioDifference(value: number): string {
    return formatPercentageDifference(value, this.selectedFilter, (key, params) => this.transloco.translate(key, params));
  }

  async getFuelLossRatioKpiStats(): Promise<void> {
    this.isLoading = true;
    this.hasError = false;

    try {
      this.request = {
        accountIds: this.widgetFilter.accountIds,
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        previousTimeFrom: this.widgetFilter.previousCycleStartDate!,
        previousTimeTo: this.widgetFilter.previousCycleEndDate!
      }

      this.cancelSubject.next();
      const response = await this.fuelChangesService.getFuelLossRatioKpiStats(this.request, this.cancelSubject);
      this.value = response.data?.totalValue ?? 0;
      this.lossRatioPercentageChange = response.data?.percentageChange;
      this.difference = response.data?.difference ?? 0;

      this.isLoading = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: (message) => {
          this.errorMessage = message;
          this.hasError = true;
          this.isLoading = false;
        }
      });
    }
  }

  openLossRatioModal(): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static',
      size: 'xl',
      windowClass: 'custom-modal-class',
    };
    const modalRef = this.modalService.open(LossRatioModal, modalOptions);
    modalRef.componentInstance.request = this.request;
    modalRef.componentInstance.modalFilterHeaders = this.modalFilterHeaders;
  }
}